import React, { useEffect, useMemo, useState, Component } from 'react';
import { X, Check, Search, Plus, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle, FileText, CreditCard, Wallet, Calendar, Pencil, Minus, Lock, ShieldCheck } from 'lucide-react';
import { Button } from '../SubscriptionAppButton';
import { TagInput } from '../TagInput';
import type { TagData } from '../TagInput';
import { useTags } from '../../contexts/TagsContext';
import { useAppData } from '../../contexts/AppDataContext';
interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (subscriptionId: string) => void;
}
interface CustomerData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  internalNote: string;
  tags: TagData[];
  acquisitionSource: string;
  acquisitionSourceDetail: string;
}
interface AddressData {
  shipping: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  billing: {
    line1: string;
    line2: string;
    city: string;
    state: string;
    postcode: string;
    country: string;
  };
  billingSameAsShipping: boolean;
}
interface SubscriptionData {
  preference: string;
  bottleCount: number;
  frequency: string;
  shippingMethod: string;
  wines: Array<{
    id: string;
    name: string;
    vintage: string;
    price: number;
    quantity: number;
  }>;
}
interface PaymentData {
  method: 'invoice' | 'saved-card';
  savedCardId?: string;
  firstBillingDate: string;
  sendWelcomeEmail: boolean;
}
const STEPS = [{
  number: 1,
  label: 'Customer'
}, {
  number: 2,
  label: 'Address'
}, {
  number: 3,
  label: 'Subscription'
}, {
  number: 4,
  label: 'Payment'
}, {
  number: 5,
  label: 'Review'
}];
const MOCK_WINES = [{
  id: '1',
  name: 'Fiano',
  vintage: '2025',
  price: 35,
  inStock: true
}, {
  id: '2',
  name: 'Shiraz',
  vintage: '2024',
  price: 45,
  inStock: true
}, {
  id: '3',
  name: 'Pinot Noir',
  vintage: '2023',
  price: 42,
  inStock: true
}, {
  id: '4',
  name: 'Chardonnay',
  vintage: '2024',
  price: 38,
  inStock: true
}, {
  id: '5',
  name: 'Cabernet Sauvignon',
  vintage: '2023',
  price: 48,
  inStock: false
}, {
  id: '6',
  name: 'Riesling',
  vintage: '2025',
  price: 32,
  inStock: true
}];
const AUSTRALIAN_STATES = ['NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
// Shipping methods synced from Shopify (mocked)
const SHIPPING_METHODS = ['Flat Rate Shipping', 'Premium Shipping', 'Priority Shipping', 'Free Shipping'];
const AVAILABLE_TAGS = ['Cellar Door Signup', 'Phone Order', 'VIP', 'Gift', 'Corporate', 'Referral'];
// Mock existing contacts for search
const MOCK_CONTACTS = [{
  id: '1',
  firstName: 'Sarah',
  lastName: 'Johnson',
  email: 'sarah.j@example.com',
  phone: '+1 (555) 123-4567',
  status: 'Active'
}, {
  id: '2',
  firstName: 'Michael',
  lastName: 'Chen',
  email: 'michael.c@example.com',
  phone: '+1 (555) 987-6543',
  status: 'Paused'
}, {
  id: '3',
  firstName: 'Emma',
  lastName: 'Wilson',
  email: 'emma.w@example.com',
  phone: '+1 (555) 456-7890',
  status: 'Active'
}, {
  id: '4',
  firstName: 'James',
  lastName: 'Brown',
  email: 'james.b@example.com',
  phone: '+1 (555) 222-3333',
  status: 'Cancelled'
}, {
  id: '5',
  firstName: 'Olivia',
  lastName: 'Davis',
  email: 'olivia.d@example.com',
  phone: '+1 (555) 777-8888',
  status: 'Active'
}];
export function CreateSubscriptionModal({
  isOpen,
  onClose,
  onSuccess
}: CreateSubscriptionModalProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdSubscriptionId, setCreatedSubscriptionId] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  // Get tags from shared context
  const {
    allTags,
    createTag
  } = useTags();
  // Get app data context for creating subscriptions
  const {
    createSubscription
  } = useAppData();
  // Form data
  const [customerData, setCustomerData] = useState<CustomerData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    internalNote: '',
    tags: [],
    acquisitionSource: 'Cellar Door',
    acquisitionSourceDetail: ''
  });
  const [addressData, setAddressData] = useState<AddressData>({
    shipping: {
      line1: '',
      line2: '',
      city: '',
      state: 'NSW',
      postcode: '',
      country: 'Australia'
    },
    billing: {
      line1: '',
      line2: '',
      city: '',
      state: 'NSW',
      postcode: '',
      country: 'Australia'
    },
    billingSameAsShipping: true
  });
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData>({
    preference: 'Custom',
    bottleCount: 3,
    frequency: 'Every Month',
    shippingMethod: 'Flat Rate Shipping',
    wines: []
  });
  const [paymentData, setPaymentData] = useState<PaymentData>({
    method: 'invoice',
    firstBillingDate: new Date().toISOString().split('T')[0],
    sendWelcomeEmail: true
  });
  // Search and validation states
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateCustomer, setShowCreateCustomer] = useState(true);
  const [emailError, setEmailError] = useState('');
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen]);
  const resetForm = () => {
    setCurrentStep(1);
    setShowSuccess(false);
    setHasUnsavedChanges(false);
    setCustomerData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      internalNote: '',
      tags: [],
      acquisitionSource: 'Cellar Door',
      acquisitionSourceDetail: ''
    });
    setAddressData({
      shipping: {
        line1: '',
        line2: '',
        city: '',
        state: 'NSW',
        postcode: '',
        country: 'Australia'
      },
      billing: {
        line1: '',
        line2: '',
        city: '',
        state: 'NSW',
        postcode: '',
        country: 'Australia'
      },
      billingSameAsShipping: true
    });
    setSubscriptionData({
      preference: 'Custom',
      bottleCount: 3,
      frequency: 'Every Month',
      shippingMethod: 'Flat Rate Shipping',
      wines: []
    });
    setPaymentData({
      method: 'invoice',
      firstBillingDate: new Date().toISOString().split('T')[0],
      sendWelcomeEmail: true
    });
  };
  const handleClose = () => {
    if (hasUnsavedChanges && !showSuccess) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };
  const handleDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };
  const handleNext = () => {
    if (validateCurrentStep()) {
      setHasUnsavedChanges(true);
      setCurrentStep(prev => Math.min(prev + 1, 5));
    }
  };
  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const handleCreateSubscription = async () => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    // Create the subscription object
    const newSubscription = {
      status: 'Active',
      customerName: `${customerData.firstName} ${customerData.lastName}`,
      customer: customerData.email,
      customerPhone: customerData.phone,
      location: `${addressData.shipping.city}, ${addressData.shipping.state}`,
      amount: `$${getSubtotal().toFixed(2)}`,
      subscriptionTitle: `${subscriptionData.preference} - ${subscriptionData.bottleCount} Pack`,
      discountPercent: 0,
      nextPayment: paymentData.firstBillingDate,
      startDate: new Date().toISOString().split('T')[0],
      orderCount: 0,
      tags: customerData.tags.map(tag => tag.id),
      items: subscriptionData.wines.map(wine => ({
        quantity: wine.quantity,
        name: `${wine.vintage} ${wine.name}`
      }))
    };
    // Create the subscription using the context
    const newSubId = createSubscription(newSubscription);
    setCreatedSubscriptionId(newSubId);
    setShowSuccess(true);
    setHasUnsavedChanges(false);
    if (onSuccess) {
      onSuccess(newSubId);
    }
  };
  const validateCurrentStep = (): boolean => {
    switch (currentStep) {
      case 1:
        return customerData.firstName.trim() !== '' && customerData.lastName.trim() !== '' && customerData.email.trim() !== '' && isValidEmail(customerData.email);
      case 2:
        return addressData.shipping.line1.trim() !== '' && addressData.shipping.city.trim() !== '' && addressData.shipping.postcode.trim() !== '';
      case 3:
        const totalBottles = subscriptionData.wines.reduce((sum, wine) => sum + wine.quantity, 0);
        return totalBottles === subscriptionData.bottleCount;
      case 4:
        return paymentData.firstBillingDate !== '';
      case 5:
        return true;
      default:
        return false;
    }
  };
  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };
  const handleEmailChange = (email: string) => {
    setCustomerData({
      ...customerData,
      email
    });
    if (email && !isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
    // Simulate duplicate check
    if (email === 'existing@example.com') {
      setDuplicateWarning(true);
    } else {
      setDuplicateWarning(false);
    }
  };
  const handleWineQuantityChange = (wineId: string, delta: number) => {
    setSubscriptionData(prev => {
      const existingWine = prev.wines.find(w => w.id === wineId);
      const wine = MOCK_WINES.find(w => w.id === wineId);
      if (!wine) return prev;
      if (!existingWine && delta > 0) {
        return {
          ...prev,
          wines: [...prev.wines, {
            id: wine.id,
            name: wine.name,
            vintage: wine.vintage,
            price: wine.price,
            quantity: 1
          }]
        };
      }
      return {
        ...prev,
        wines: prev.wines.map(w => {
          if (w.id === wineId) {
            const newQuantity = Math.max(0, w.quantity + delta);
            return {
              ...w,
              quantity: newQuantity
            };
          }
          return w;
        }).filter(w => w.quantity > 0)
      };
    });
  };
  const getWineQuantity = (wineId: string): number => {
    return subscriptionData.wines.find(w => w.id === wineId)?.quantity || 0;
  };
  const getTotalBottles = (): number => {
    return subscriptionData.wines.reduce((sum, wine) => sum + wine.quantity, 0);
  };
  const getSubtotal = (): number => {
    return subscriptionData.wines.reduce((sum, wine) => sum + wine.price * wine.quantity, 0);
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-1788" data-mp="2aot1zyu2w6zk">
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#0F172A]/40 backdrop-blur-sm" onClick={handleClose} data-id="element-1789" data-mp="25pghvlulde0o" />

      {/* Modal */}
      <div className="relative w-full max-w-[680px] bg-white rounded-xl shadow-xl flex flex-col max-h-[90vh]" data-id="element-1790" data-mp="2e7s50eavrd98">
        {showSuccess ? <SuccessView subscriptionId={createdSubscriptionId} customerName={`${customerData.firstName} ${customerData.lastName}`} onViewSubscription={() => {
        // Close modal and return to subscriptions table
        if (onSuccess) {
          onSuccess(createdSubscriptionId);
        }
        onClose();
      }} onCreateAnother={() => {
        resetForm();
      }} data-id="element-1791" data-mp="2i711vs17vr90" /> : <>
            {/* Header */}
            <div className="flex-shrink-0 px-6 py-5 border-b border-[#E2E8F0]" data-id="element-1792" data-mp="1zbsqakt9mev4">
              <div className="flex items-center justify-between" data-id="element-1793" data-mp="24783dajg86qh">
                <h2 className="text-[20px] font-semibold text-[#0F172A]" data-id="element-1794" data-mp="2985vofy6ah5p">
                  Create Subscription
                </h2>
                <button onClick={handleClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-1795" data-mp="3fng7hgej3q49">
                  <X className="w-5 h-5" data-id="element-1796" data-mp="2uynfl5v35ku4" />
                </button>
              </div>
            </div>

            {/* Step Indicator */}
            <StepIndicator currentStep={currentStep} completedSteps={currentStep - 1} data-id="element-1797" data-mp="2pz12cwf9c4lb" />

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1798" data-mp="34wbn6wdsimtp">
              {currentStep === 1 && <Step1Customer data={customerData} onChange={setCustomerData} searchQuery={searchQuery} onSearchChange={setSearchQuery} showCreateCustomer={showCreateCustomer} onToggleCreateCustomer={setShowCreateCustomer} emailError={emailError} onEmailChange={handleEmailChange} duplicateWarning={duplicateWarning} allTags={allTags} onCreateTag={createTag} data-id="element-1799" data-mp="2zzbd8adtkzxb" />}
              {currentStep === 2 && <Step2Address data={addressData} onChange={setAddressData} data-id="element-1800" data-mp="2h6hbii57j4b1" />}
              {currentStep === 3 && <Step3Subscription data={subscriptionData} onChange={setSubscriptionData} onWineQuantityChange={handleWineQuantityChange} getWineQuantity={getWineQuantity} getTotalBottles={getTotalBottles} data-id="element-1801" data-mp="1tgweivc7y6ir" />}
              {currentStep === 4 && <Step4Payment data={paymentData} onChange={setPaymentData} data-id="element-1802" data-mp="3zho4hnd0std" />}
              {currentStep === 5 && <Step5Review customerData={customerData} addressData={addressData} subscriptionData={subscriptionData} paymentData={paymentData} onEdit={setCurrentStep} getSubtotal={getSubtotal} data-id="element-1803" data-mp="8t3trpv3vwfo" />}
            </div>

            {/* Footer */}
            <div className="flex-shrink-0 px-6 py-4 bg-[#F8FAFC] border-t border-[#E2E8F0] flex items-center justify-between" data-id="element-1804" data-mp="mrjw74lxr0s0">
              <Button variant="outline" size="md" onClick={handleBack} disabled={currentStep === 1} iconLeft={<ChevronLeft className="w-4 h-4" data-id="element-1806" data-mp="drdm7spg4ixl" />} data-id="element-1805" data-mp="rpbzsvvy0bc1">
                Back
              </Button>
              <Button variant="primary" size="md" onClick={currentStep === 5 ? handleCreateSubscription : handleNext} disabled={!validateCurrentStep()} iconRight={currentStep < 5 ? <ChevronRight className="w-4 h-4" data-id="element-1808" data-mp="32ig7oi12gby9" /> : undefined} data-id="element-1807" data-mp="hrfrscyaxwi5">
                {currentStep === 5 ? 'Create Subscription' : 'Next'}
              </Button>
            </div>
          </>}
      </div>

      {/* Discard Confirmation */}
      {showDiscardConfirm && <DiscardConfirmation onCancel={() => setShowDiscardConfirm(false)} onDiscard={handleDiscard} data-id="element-1809" data-mp="3dy2qv3lhhzr9" />}
    </div>;
}
// Step Indicator Component
function StepIndicator({
  currentStep,
  completedSteps
}: {
  currentStep: number;
  completedSteps: number;
}) {
  return <div className="flex-shrink-0 px-6 py-4 border-b border-[#E2E8F0]" data-id="element-1810" data-mp="5jknhf6hn8dz">
      <div className="flex items-center justify-between relative" data-id="element-1811" data-mp="5gonllb2y9mw">
        {/* Progress Bar */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-[#E2E8F0]" data-id="element-1812" data-mp="i52fn4b91vl">
          <div className="h-full bg-[#0EA5E9] transition-all duration-300" style={{
          width: `${completedSteps / (STEPS.length - 1) * 100}%`
        }} data-id="element-1813" data-mp="3rooch02bonsu" />
        </div>

        {/* Steps */}
        {STEPS.map((step, index) => {
        const isCompleted = step.number < currentStep;
        const isCurrent = step.number === currentStep;
        const isUpcoming = step.number > currentStep;
        return <div key={step.number} className="flex flex-col items-center relative z-10" data-id="element-1814" data-mp="rtawcm82jmxa" data-mpi={index}>
              <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm
                  transition-all duration-200
                  ${isCompleted ? 'bg-[#0EA5E9] text-white' : isCurrent ? 'bg-[#0EA5E9] text-white scale-110' : 'bg-white border-2 border-[#CBD5E1] text-[#94A3B8]'}
                `} data-id="element-1815" data-mp="vtl5g7r5qtgd">
                {isCompleted ? <Check className="w-5 h-5" data-id="element-1816" data-mp="1ffzk3ojre9lt" /> : <span data-id="element-1817" data-mp="1ff7o4cm929v0">{step.number}</span>}
              </div>
              <span className={`
                  mt-2 text-xs font-medium hidden sm:block
                  ${isCurrent ? 'text-[#0EA5E9]' : 'text-[#64748B]'}
                `} data-id="element-1818" data-mp="1jie2k7jin7ck">
                {step.label}
              </span>
            </div>;
      })}
      </div>
    </div>;
}
// Step 1: Customer
function Step1Customer({
  data,
  onChange,
  searchQuery,
  onSearchChange,
  showCreateCustomer,
  onToggleCreateCustomer,
  emailError,
  onEmailChange,
  duplicateWarning,
  allTags,
  onCreateTag
}: any) {
  const [showResults, setShowResults] = useState(false);
  const [selectedContact, setSelectedContact] = useState<(typeof MOCK_CONTACTS)[0] | null>(null);
  const filteredContacts = useMemo(() => {
    if (!searchQuery || searchQuery.trim().length < 1) return [];
    const q = searchQuery.toLowerCase();
    return MOCK_CONTACTS.filter(c => `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) || c.email.toLowerCase().includes(q) || c.phone.includes(q));
  }, [searchQuery]);
  const handleSelectContact = (contact: (typeof MOCK_CONTACTS)[0]) => {
    setSelectedContact(contact);
    setShowResults(false);
    onSearchChange('');
    onChange({
      ...data,
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email,
      phone: contact.phone
    });
    onToggleCreateCustomer(true);
  };
  const handleClearSelection = () => {
    setSelectedContact(null);
    onChange({
      ...data,
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    });
  };
  const handleAddTag = (tag: TagData) => {
    onChange({
      ...data,
      tags: [...data.tags, tag]
    });
  };
  const handleRemoveTag = (tagId: string) => {
    onChange({
      ...data,
      tags: data.tags.filter((t: TagData) => t.id !== tagId)
    });
  };
  const handleCreateNewTag = (label: string, color?: string) => {
    const newTag: TagData = {
      id: label.toLowerCase().replace(/\s+/g, '-'),
      label,
      color: (color || 'blue') as 'blue' | 'green' | 'orange' | 'purple' | 'gray'
    };
    onCreateTag(newTag);
    handleAddTag(newTag);
  };
  return <div className="space-y-6" data-id="element-1819" data-mp="1q1v7f9zt52gq">
      <div data-id="element-1820" data-mp="1pyz4f793rnlf">
        <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1821" data-mp="1c1ali4yliqj0">
          Find or create customer
        </label>

        {/* Selected contact chip */}
        {selectedContact && <div className="mb-2 flex items-center gap-2 px-3 py-2 bg-[#F0F9FF] border border-[#BAE6FD] rounded-md" data-id="element-1822" data-mp="1g50bmyux5g65">
            <div className="flex-1" data-id="element-1823" data-mp="1rjbxa2chisgh">
              <span className="text-sm font-medium text-[#0F172A]" data-id="element-1824" data-mp="1rkdbugsplx2y">
                {selectedContact.firstName} {selectedContact.lastName}
              </span>
              <span className="text-sm text-[#64748B] ml-2" data-id="element-1825" data-mp="1hl5wjo2ezsd7">
                {selectedContact.email}
              </span>
            </div>
            <button onClick={handleClearSelection} className="text-[#94A3B8] hover:text-[#0F172A] transition-colors" data-id="element-1826" data-mp="1dklzekhucor2">
              <X className="w-4 h-4" data-id="element-1827" data-mp="18r9fgno3x64n" />
            </button>
          </div>}

        <div className="relative" data-id="element-1828" data-mp="13usq5q568lpz">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" data-id="element-1829" data-mp="13u0t0avxefxl" />
          <input type="text" value={searchQuery} onChange={e => {
          onSearchChange(e.target.value);
          setShowResults(true);
        }} onFocus={() => setShowResults(true)} placeholder="Search by name, email, or phone..." className="w-full h-10 pl-10 pr-4 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1830" data-mp="ywheraz8a35n" />

          {/* Search Results Dropdown */}
          {showResults && searchQuery.trim().length > 0 && <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-[0_4px_12px_rgba(15,23,42,0.1)] z-50 max-h-[240px] overflow-y-auto" data-id="element-1831" data-mp="3aij9vithmiyu">
              {filteredContacts.length > 0 ? <>
                  <div className="px-3 py-2 text-[11px] font-semibold text-[#94A3B8] uppercase tracking-wider border-b border-[#E2E8F0]" data-id="element-1832" data-mp="2msf9rlfid1cl">
                    Existing Contacts
                  </div>
                  {filteredContacts.map(contact => <button key={contact.id} onClick={() => handleSelectContact(contact)} className="w-full text-left px-3 py-2.5 hover:bg-[#F8FAFC] transition-colors flex items-center gap-3 border-b border-[#F1F5F9] last:border-b-0" data-id="element-1833" data-mp="2wja6xkuallct">
                      <div className="w-8 h-8 rounded-full bg-[#E0F2FE] flex items-center justify-center flex-shrink-0" data-id="element-1834" data-mp="2403a56g8oe65">
                        <span className="text-xs font-semibold text-[#0284C7]" data-id="element-1835" data-mp="1gyl6opvcntnj">
                          {contact.firstName[0]}
                          {contact.lastName[0]}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0" data-id="element-1836" data-mp="1qx0zqydxtczo">
                        <div className="text-sm font-medium text-[#0F172A]" data-id="element-1837" data-mp="1quetd22s93j1">
                          {contact.firstName} {contact.lastName}
                        </div>
                        <div className="text-xs text-[#64748B] truncate" data-id="element-1838" data-mp="y8ljk8j2komr">
                          {contact.email} · {contact.phone}
                        </div>
                      </div>
                      <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${contact.status === 'Active' ? 'bg-[#D1FAE5] text-[#059669]' : contact.status === 'Paused' ? 'bg-[#FEF3C7] text-[#D97706]' : 'bg-[#F1F5F9] text-[#64748B]'}`} data-id="element-1839" data-mp="17yoliy865cx8">
                        {contact.status}
                      </span>
                    </button>)}
                </> : <div className="px-3 py-4 text-center" data-id="element-1840" data-mp="2oqjskmb3vyrn">
                  <p className="text-sm text-[#64748B]" data-id="element-1841" data-mp="2oqs9cpacno84">
                    No contacts found for "{searchQuery}"
                  </p>
                </div>}
              {/* Create new option */}
              <div className="border-t border-[#E2E8F0]" data-id="element-1842" data-mp="2erkrboso75eo">
                <button onClick={() => {
              setShowResults(false);
              setSelectedContact(null);
              onToggleCreateCustomer(true);
            }} className="w-full text-left px-3 py-2.5 hover:bg-[#F0F9FF] transition-colors flex items-center gap-2 text-[#0EA5E9]" data-id="element-1843" data-mp="29tjdpje0jyal">
                  <Plus className="w-4 h-4" data-id="element-1844" data-mp="1w24rl3ot7gzb" />
                  <span className="text-sm font-medium" data-id="element-1845" data-mp="1w3fvd3i98oqf">
                    Create new customer
                  </span>
                </button>
              </div>
            </div>}
        </div>
        {!showCreateCustomer && !selectedContact && <button onClick={() => onToggleCreateCustomer(true)} className="mt-2 text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium flex items-center gap-1" data-id="element-1846" data-mp="1cft889k84pb5">
            <Plus className="w-4 h-4" data-id="element-1847" data-mp="1hg7dxkycrn60" />
            Create new customer
          </button>}
      </div>

      {showCreateCustomer && <>
          <div className="grid grid-cols-2 gap-4" data-id="element-1848" data-mp="17ggj1i5vr65e">
            <div data-id="element-1849" data-mp="17h8emszh93vo">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1850" data-mp="17isynoo614fa">
                First Name <span className="text-[#EF4444]" data-id="element-1851" data-mp="1v72od1oi2cbp">*</span>
              </label>
              <input type="text" value={data.firstName} onChange={e => onChange({
            ...data,
            firstName: e.target.value
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1852" data-mp="2042o6uuraiud" />
            </div>
            <div data-id="element-1853" data-mp="2e32avwypknnw">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1854" data-mp="2ivvfmku6fvzz">
                Last Name <span className="text-[#EF4444]" data-id="element-1855" data-mp="7gwkjqkz03bb">*</span>
              </label>
              <input type="text" value={data.lastName} onChange={e => onChange({
            ...data,
            lastName: e.target.value
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1856" data-mp="7f397xzewuwz" />
            </div>
          </div>

          <div data-id="element-1857" data-mp="3kmwm5valuh5v">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1858" data-mp="3kohhvo8no65b">
              Email <span className="text-[#EF4444]" data-id="element-1859" data-mp="3fov7rno4j60q">*</span>
            </label>
            <input type="email" value={data.email} onChange={e => onEmailChange(e.target.value)} className={`w-full h-10 px-3 bg-white border rounded-md text-sm focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none ${emailError ? 'border-[#EF4444]' : 'border-[#CBD5E1] focus:border-[#0EA5E9]'}`} data-id="element-1860" data-mp="3ulm5lsi5g9o8" />
            {emailError && <p className="mt-1 text-xs text-[#EF4444]" data-id="element-1861" data-mp="14wk691e1rsw4">{emailError}</p>}
          </div>

          {duplicateWarning && <div className="p-3 bg-[#FFFBEB] border border-[#FEF3C7] rounded-md flex gap-2" data-id="element-1862" data-mp="3al4271b3rk0f">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0" data-id="element-1863" data-mp="3fje1no3v16ru" />
              <div className="flex-1" data-id="element-1864" data-mp="3fli9ui8skrxx">
                <p className="text-sm text-[#92400E]" data-id="element-1865" data-mp="3jk7mg8m0a5gi">
                  This customer already has an active subscription. Creating
                  another will give them multiple subscriptions.
                </p>
                <button className="mt-1 text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium" data-id="element-1866" data-mp="3tinbm61h34nd">
                  View existing subscription →
                </button>
              </div>
            </div>}

          <div data-id="element-1867" data-mp="74dr480nygrv">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1868" data-mp="75ymu0yps5rb">
              Phone
            </label>
            <input type="tel" value={data.phone} onChange={e => onChange({
          ...data,
          phone: e.target.value
        })} placeholder="+61 4XX XXX XXX" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1869" data-mp="g4k3fqkmlapg" />
          </div>

          <div data-id="element-1870" data-mp="q7fkgi4rcmpl">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1871" data-mp="q6w3p4qug4d5">
              Internal Note
            </label>
            <textarea value={data.internalNote} onChange={e => onChange({
          ...data,
          internalNote: e.target.value
        })} placeholder="Add notes about this subscription (e.g., Phone order - spoke with John)" rows={3} className="w-full px-3 py-2 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none resize-none" data-id="element-1872" data-mp="lb7b5ld8rnqc" />
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-1873" data-mp="106my76femjd8">
              Only visible to staff, not the customer
            </p>
          </div>

          <div data-id="element-1874" data-mp="1wpkuqe0yph0k">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1875" data-mp="1wq4250szkn0c">
              Tags
            </label>
            <TagInput tags={data.tags} availableTags={allTags} onAddTag={handleAddTag} onRemoveTag={handleRemoveTag} onCreateTag={handleCreateNewTag} placeholder="Add or create tag..." data-id="element-1876" data-mp="1rvq38of47xqx" />
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-1877" data-mp="3r482wr724wqo">
              Type a tag name and press Enter to create a new tag
            </p>
          </div>

          <div data-id="element-1878" data-mp="3heg7jur4vzl5">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1879" data-mp="3hdwqshd7zh8p">
              Acquisition Source
            </label>
            <select value={data.acquisitionSource} onChange={e => {
          onChange({
            ...data,
            acquisitionSource: e.target.value,
            acquisitionSourceDetail: ''
          });
        }} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1880" data-mp="33dvqb30vabtr">
              <option value="Cellar Door" data-id="element-1881" data-mp="2m5kyze299lo3">Cellar Door</option>
              <option value="Website" data-id="element-1882" data-mp="2m41mzznmtws4">Website</option>
              <option value="Event" data-id="element-1883" data-mp="2zvy7bhj272dy">Event</option>
              <option value="Referral" data-id="element-1884" data-mp="2vvo2w9ax0k17">Referral</option>
              <option value="Phone" data-id="element-1885" data-mp="2vv4k6qdfxrm4">Phone</option>
              <option value="Other" data-id="element-1886" data-mp="39v70i863ocz9">Other</option>
            </select>
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-1887" data-mp="39tcnrh4zfr69">
              Where did this member come from?
            </p>

            {data.acquisitionSource === 'Event' && <div className="mt-3" data-id="element-1888" data-mp="1fih3hhkfqnnn">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1889" data-mp="1fjih9tsu5o92">
                  Event Name
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Winter Wine Festival 2026" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1890" data-mp="opp4ouabn9dt" />
              </div>}

            {data.acquisitionSource === 'Referral' && <div className="mt-3" data-id="element-1891" data-mp="5ruq80qfc5o1">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1892" data-mp="ape95dy2h6mb">
                  Referred by
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Member name or email" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1893" data-mp="fnfslzzen7yb" />
              </div>}

            {data.acquisitionSource === 'Other' && <div className="mt-3" data-id="element-1894" data-mp="3gmujeawf61io">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1895" data-mp="3bl5b330l01ya">
                  Source
                </label>
                <input type="text" value={data.acquisitionSourceDetail} onChange={e => onChange({
            ...data,
            acquisitionSourceDetail: e.target.value
          })} placeholder="e.g. Trade show, partner promotion" className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1896" data-mp="36tdsbeno30ip" />
              </div>}
          </div>
        </>}
    </div>;
}
// Step 2: Address
function Step2Address({
  data,
  onChange
}: any) {
  return <div className="space-y-6" data-id="element-1897" data-mp="2iz421kdo4g9a">
      <div data-id="element-1898" data-mp="m4hc1fbrqxx2">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1899" data-mp="m4zi13o8gqi6">
          Shipping Address
        </h3>
        <div className="space-y-4" data-id="element-1900" data-mp="h6o86sxxufzy">
          <div data-id="element-1901" data-mp="h6fxn7pafmrx">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1902" data-mp="h3tq382ed79p">
              Address Line 1 <span className="text-[#EF4444]" data-id="element-1903" data-mp="17d7bq6dm926r">*</span>
            </label>
            <input type="text" value={data.shipping.line1} onChange={e => onChange({
            ...data,
            shipping: {
              ...data.shipping,
              line1: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1904" data-mp="12fxngu1yqwki" />
          </div>

          <div data-id="element-1905" data-mp="jj5zw8fe1van">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1906" data-mp="jjpjruolmtrb">
              Address Line 2
            </label>
            <input type="text" value={data.shipping.line2} onChange={e => onChange({
            ...data,
            shipping: {
              ...data.shipping,
              line2: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1907" data-mp="tg0u4f4mp9j4" />
          </div>

          <div className="grid grid-cols-2 gap-4" data-id="element-1908" data-mp="2hda4lmnpv3x0">
            <div data-id="element-1909" data-mp="2hf4ocxn94pzi">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1910" data-mp="2hbgutyht43ks">
                City <span className="text-[#EF4444]" data-id="element-1911" data-mp="22i3v2ppewmyx">*</span>
              </label>
              <input type="text" value={data.shipping.city} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                city: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1912" data-mp="27g5g3g6eewd1" />
            </div>
            <div data-id="element-1913" data-mp="2qcneuhjnzwis">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1914" data-mp="2qc582qzdlzwm">
                State <span className="text-[#EF4444]" data-id="element-1915" data-mp="3e2hqivs8zf1g">*</span>
              </label>
              <select value={data.shipping.state} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                state: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1916" data-mp="3ivau3gbzchby">
                {AUSTRALIAN_STATES.map(state => <option key={state} value={state} data-id="element-1917" data-mp="2qzuk3ohuq48f">
                    {state}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4" data-id="element-1918" data-mp="11kaaqe157yhu">
            <div data-id="element-1919" data-mp="11js9t43jcw3l">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1920" data-mp="11mef0x2yezin">
                Postcode <span className="text-[#EF4444]" data-id="element-1921" data-mp="16l7swqm5ksjc">*</span>
              </label>
              <input type="text" value={data.shipping.postcode} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                postcode: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1922" data-mp="1bipshqea5veo" />
            </div>
            <div className="col-span-3" data-id="element-1923" data-mp="rs4cwno7mmuq">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1924" data-mp="wlh14woz8rmy">
                Country <span className="text-[#EF4444]" data-id="element-1925" data-mp="3b0z24ubvqy">*</span>
              </label>
              <select value={data.shipping.country} onChange={e => onChange({
              ...data,
              shipping: {
                ...data.shipping,
                country: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1926" data-mp="187g84hbkhh">
                <option value="Australia" data-id="element-1927" data-mp="8q5b8np9indz">Australia</option>
                <option value="New Zealand" data-id="element-1928" data-mp="2ffa1tp1a7l5s">New Zealand</option>
                <option value="United States" data-id="element-1929" data-mp="2fhwjwilstj29">United States</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div data-id="element-1930" data-mp="pzzcllixsscz">
        <label className="flex items-center gap-2 cursor-pointer" data-id="element-1931" data-mp="q093dbbt33jq">
          <input type="checkbox" checked={data.billingSameAsShipping} onChange={e => onChange({
          ...data,
          billingSameAsShipping: e.target.checked
        })} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-1932" data-mp="uyizpp99oa6x" />
          <span className="text-sm text-[#0F172A]" data-id="element-1933" data-mp="l64v8oqt0ph2">
            Billing address same as shipping
          </span>
        </label>
      </div>

      {!data.billingSameAsShipping && <div data-id="element-1934" data-mp="bblchicwni04">
          <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1935" data-mp="bbd0dsolwgpz">
            Billing Address
          </h3>
          <div className="space-y-4" data-id="element-1936" data-mp="3thzohbrm5hvv">
            <div data-id="element-1937" data-mp="28k75jvv5uxi">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1938" data-mp="2a50j66fz7ts">
                Address Line 1 <span className="text-[#EF4444]" data-id="element-1939" data-mp="1rpl9olkd8tjo">*</span>
              </label>
              <input type="text" value={data.billing.line1} onChange={e => onChange({
            ...data,
            billing: {
              ...data.billing,
              line1: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1940" data-mp="1wnmsd5dvqqum" />
            </div>

            <div data-id="element-1941" data-mp="1g9qcrdsptckr">
              <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1942" data-mp="1ga8hyzxcv14t">
                Address Line 2
              </label>
              <input type="text" value={data.billing.line2} onChange={e => onChange({
            ...data,
            billing: {
              ...data.billing,
              line2: e.target.value
            }
          })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1943" data-mp="2dyryo6cqk9mb" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-1944" data-mp="2wp9rsc1hd787">
              <div data-id="element-1945" data-mp="2rvfatgd9f6ct">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1946" data-mp="2rux6dwgg1ltt">
                  City <span className="text-[#EF4444]" data-id="element-1947" data-mp="306flt5k75h6p">*</span>
                </label>
                <input type="text" value={data.billing.city} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                city: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1948" data-mp="345xy5cxycjxx" />
              </div>
              <div data-id="element-1949" data-mp="3n494bvtno8cq">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1950" data-mp="3n3qzwbwuantq">
                  State <span className="text-[#EF4444]" data-id="element-1951" data-mp="3rwttul5j3zag">*</span>
                </label>
                <select value={data.billing.state} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                state: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1952" data-mp="oxtyn768tfg">
                  {AUSTRALIAN_STATES.map(state => <option key={state} value={state} data-id="element-1953" data-mp="m1zbdrsywlbi">
                      {state}
                    </option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4" data-id="element-1954" data-mp="3ppivq8vlc0hu">
              <div data-id="element-1955" data-mp="3po7v2hxfz8uy">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1956" data-mp="3pnocr03vqige">
                  Postcode <span className="text-[#EF4444]" data-id="element-1957" data-mp="3kp3jjkn1oz1g">*</span>
                </label>
                <input type="text" value={data.billing.postcode} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                postcode: e.target.value
              }
            })} className="w-full h-10 px-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-1958" data-mp="207m1acdtv49z" />
              </div>
              <div className="col-span-3" data-id="element-1959" data-mp="2jv1va9uh9t2y">
                <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1960" data-mp="2nxgbem6rieqj">
                  Country <span className="text-[#EF4444]" data-id="element-1961" data-mp="29zhyll83aqd5">*</span>
                </label>
                <select value={data.billing.country} onChange={e => onChange({
              ...data,
              billing: {
                ...data.billing,
                country: e.target.value
              }
            })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1962" data-mp="2exjl6g4q57td">
                  <option value="Australia" data-id="element-1963" data-mp="19iw3s4el9i6q">Australia</option>
                  <option value="New Zealand" data-id="element-1964" data-mp="vkfohp2nujcz">New Zealand</option>
                  <option value="United States" data-id="element-1965" data-mp="zmj66vfxak6p">United States</option>
                </select>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
// Step 3: Subscription
function Step3Subscription({
  data,
  onChange,
  onWineQuantityChange,
  getWineQuantity,
  getTotalBottles
}: any) {
  const totalBottles = getTotalBottles();
  const isValid = totalBottles === data.bottleCount;
  return <div className="space-y-6" data-id="element-1966" data-mp="37v7g8b89o31e">
      <div className="flex justify-end" data-id="element-1967" data-mp="37twai5v9gl7n">
        <span className="px-3 py-1.5 bg-[#E0F2FE] text-[#0284C7] text-sm font-medium rounded-full" data-id="element-1968" data-mp="3mreyv7410wek">
          {data.bottleCount} Bottles {data.frequency}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-4" data-id="element-1969" data-mp="3hqrakt76mto1">
        <div data-id="element-1970" data-mp="2u42nlt3bgb0k">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1971" data-mp="2u3sxm5i9u3uv">
            Preference
          </label>
          <select value={data.preference} onChange={e => onChange({
          ...data,
          preference: e.target.value
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1972" data-mp="2p5rczg575wha">
            <option value="Custom" data-id="element-1973" data-mp="39rnxlbqwk0qb">Custom</option>
            <option value="Winery Selection" data-id="element-1974" data-mp="2d2q2fsf8q0sm">Winery Selection</option>
          </select>
        </div>

        <div data-id="element-1975" data-mp="2h4bni11xhldw">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1976" data-mp="2h3s2uckw8iw6">
            Bottle Count
          </label>
          <select value={data.bottleCount} onChange={e => onChange({
          ...data,
          bottleCount: parseInt(e.target.value)
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1977" data-mp="2m2d48fbvt8m7">
            <option value={3} data-id="element-1978" data-mp="184cqu9iyh8yw">3 Bottles</option>
            <option value={6} data-id="element-1979" data-mp="1854hr71lyiiu">6 Bottles</option>
            <option value={12} data-id="element-1980" data-mp="13473v6nmsan5">12 Bottles</option>
          </select>
        </div>

        <div data-id="element-1981" data-mp="y5dp7awlydle">
          <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1982" data-mp="y53yfl3qo2en">
            Frequency
          </label>
          <select value={data.frequency} onChange={e => onChange({
          ...data,
          frequency: e.target.value
        })} className="w-full h-10 pl-3 pr-10 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23475569%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E')] bg-[length:20px_20px] bg-[right_12px_center] bg-no-repeat" data-id="element-1983" data-mp="t7lxagvyqrh7">
            <option value="Every Month" data-id="element-1984" data-mp="2hl8esw8fzi4l">Every Month</option>
            <option value="Every 2 Months" data-id="element-1985" data-mp="2hmta4o2kz53i">Every 2 Months</option>
            <option value="Every 3 Months" data-id="element-1986" data-mp="22xn8yuk6n4sz">Every 3 Months</option>
            <option value="Quarterly" data-id="element-1987" data-mp="27vfcklu6wfd0">Quarterly</option>
            <option value="Every 6 Months" data-id="element-1988" data-mp="27w70dahjp8sq">Every 6 Months</option>
            <option value="Yearly" data-id="element-1989" data-mp="1tvnsq4gzs4r3">Yearly</option>
          </select>
        </div>
      </div>

      <div data-id="element-1990" data-mp="1xxr5cwfedfdy">
        <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-1991" data-mp="1xwg3j25iihph">
          Shipping Method
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-1992" data-mp="1f9qpzgs2pvck">
          {SHIPPING_METHODS.map(method => {
          const selected = data.shippingMethod === method;
          return <label key={method} className={`flex items-center gap-2.5 h-10 px-3 bg-white border rounded-md text-sm cursor-pointer transition-all duration-200 ${selected ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9]/20' : 'border-[#CBD5E1] hover:border-[#94A3B8]'}`} data-id="element-1993" data-mp="1oa5boh8y9cgl">
                <input type="radio" name="shippingMethod" value={method} checked={selected} onChange={e => onChange({
              ...data,
              shippingMethod: e.target.value
            })} className="w-4 h-4 text-[#0EA5E9] border-[#CBD5E1] focus:ring-2 focus:ring-[#0EA5E9]/20 cursor-pointer" data-id="element-1994" data-mp="1t8zu3i308z06" />
                <span className={selected ? 'text-[#0F172A] font-medium' : 'text-[#475569]'} data-id="element-1995" data-mp="2gyayranh7djl">
                  {method}
                </span>
              </label>;
        })}
        </div>
        <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-1996" data-mp="19m1vizbbgech">Synced from Shopify</p>
      </div>

      <div data-id="element-1997" data-mp="19k8kz8xl19z7">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-1998" data-mp="1dnn4waon6ahx">
          Select Wines
        </h3>
        <div className="border border-[#E2E8F0] rounded-lg divide-y divide-[#E2E8F0]" data-id="element-1999" data-mp="1in01hn5bevxp">
          {MOCK_WINES.map(wine => {
          const quantity = getWineQuantity(wine.id);
          const isSelected = quantity > 0;
          return <div key={wine.id} className={`p-4 flex items-center justify-between ${isSelected ? 'bg-[#F0F9FF]' : ''} ${!wine.inStock ? 'opacity-50' : ''}`} data-id="element-2000" data-mp="1sbhw1mn046sw">
                <div className="flex-1" data-id="element-2001" data-mp="21d7zjqu5qw71">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-2002" data-mp="21eaqfyrhcymy">
                    {wine.vintage} {wine.name}
                  </div>
                  <div className="text-sm text-[#64748B] mt-0.5" data-id="element-2003" data-mp="26a82vtzmhkug">
                    ${wine.price.toFixed(2)}
                  </div>
                  {!wine.inStock && <span className="inline-block mt-1 px-2 py-0.5 bg-[#F1F5F9] text-[#64748B] text-xs rounded" data-id="element-2004" data-mp="bzcky12jstf0">
                      Out of stock
                    </span>}
                </div>
                <div className="flex items-center gap-2" data-id="element-2005" data-mp="3vt6ac0hypdmo">
                  <button onClick={() => onWineQuantityChange(wine.id, -1)} disabled={!wine.inStock || quantity === 0} className="w-8 h-8 flex items-center justify-center border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2006" data-mp="3vrmuuc44r6ly">
                    <Minus className="w-4 h-4" data-id="element-2007" data-mp="ncv1v1804a3i" />
                  </button>
                  <span className="w-8 text-center text-sm font-medium" data-id="element-2008" data-mp="ifuwytmw1ddz">
                    {quantity}
                  </span>
                  <button onClick={() => onWineQuantityChange(wine.id, 1)} disabled={!wine.inStock} className="w-8 h-8 flex items-center justify-center border border-[#CBD5E1] rounded-md hover:bg-[#F1F5F9] disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2009" data-mp="dhjn4iwlf2vr">
                    <Plus className="w-4 h-4" data-id="element-2010" data-mp="2u3ugvqui3i45" />
                  </button>
                </div>
              </div>;
        })}
        </div>
      </div>

      {/* Validation Banner */}
      <div className={`p-3 rounded-md flex items-center gap-2 ${isValid ? 'bg-[#D1FAE5] border border-[#10B981]' : 'bg-[#FEE2E2] border border-[#EF4444]'}`} data-id="element-2011" data-mp="122az7nomp003">
        {isValid ? <>
            <CheckCircle className="w-5 h-5 text-[#059669] flex-shrink-0" data-id="element-2012" data-mp="t1v1yvyhap3o" />
            <span className="text-sm text-[#065F46]" data-id="element-2013" data-mp="t3z3564zta09">
              Bottle count is correct - {totalBottles} of {data.bottleCount}{' '}
              bottles selected
            </span>
          </> : <>
            <AlertTriangle className="w-5 h-5 text-[#DC2626] flex-shrink-0" data-id="element-2014" data-mp="1kv5aa6j4s7z1" />
            <span className="text-sm text-[#991B1B]" data-id="element-2015" data-mp="1714f1mre133f">
              Please select {data.bottleCount} bottles (currently {totalBottles}{' '}
              selected)
            </span>
          </>}
      </div>
    </div>;
}
// Step 4: Payment
function Step4Payment({
  data,
  onChange
}: any) {
  const [selectedCardId, setSelectedCardId] = useState('card-1');
  const [hasSavedCards, setHasSavedCards] = useState(true);
  const MOCK_SAVED_CARDS = [{
    id: 'card-1',
    brand: 'Visa',
    last4: '4242',
    exp: '12/27'
  }, {
    id: 'card-2',
    brand: 'Mastercard',
    last4: '8888',
    exp: '03/26'
  }];
  return <div className="space-y-6" data-id="element-2016" data-mp="2nnr679cpypwc">
      <div data-id="element-2017" data-mp="2imtspa2nmk16">
        <div className="flex items-center justify-between mb-4" data-id="element-2018" data-mp="2ima79jdspdie">
          <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-2019" data-mp="2iowhjqs2m74b">
            Payment Method
          </h3>
          {/* Demo toggle for saved cards state */}
          <label className="flex items-center gap-2 cursor-pointer select-none" data-id="element-2020" data-mp="1qwp6r6z8xoxu">
            <span className="text-[11px] font-medium text-[#94A3B8] uppercase tracking-wide" data-id="element-2021" data-mp="24w6qv91lke0v">
              Has saved cards
            </span>
            <button role="switch" aria-checked={hasSavedCards} onClick={() => setHasSavedCards(!hasSavedCards)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ${hasSavedCards ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-2022" data-mp="1zy5a4urn8qsk">
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${hasSavedCards ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} data-id="element-2023" data-mp="147badr6yox76" />
            </button>
          </label>
        </div>
        <div className="space-y-3" data-id="element-2024" data-mp="q6qqsst82xcm">
          <PaymentOption icon={<FileText className="w-5 h-5" data-id="element-2026" data-mp="q8blcifjegah" />} value="invoice" selected={data.method === 'invoice'} onSelect={() => onChange({
          ...data,
          method: 'invoice'
        })} title="Send invoice" description="Customer will receive a Shopify checkout link to complete payment" data-id="element-2025" data-mp="q7sebwrfdcb8" />

          {/* Saved Card - Expandable */}
          <div className={`border-2 rounded-lg transition-all ${data.method === 'saved-card' ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-2027" data-mp="1e2cyqktexv8p">
            <button onClick={() => onChange({
            ...data,
            method: 'saved-card'
          })} className="w-full p-4 text-left" data-id="element-2028" data-mp="26mjyxnibxf9z">
              <div className="flex items-start gap-3" data-id="element-2029" data-mp="295oq9maa4b6p">
                <div className={`flex-shrink-0 ${data.method === 'saved-card' ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-2030" data-mp="2979g4ylnf5y8">
                  <Wallet className="w-5 h-5" data-id="element-2031" data-mp="2e6cmqnha1k8b" />
                </div>
                <div className="flex-1" data-id="element-2032" data-mp="2j21i0em9keyt">
                  <div className="text-sm font-medium text-[#0F172A]" data-id="element-2033" data-mp="2n4y3cd36up6g">
                    Use saved payment method
                  </div>
                  <div className="text-sm text-[#64748B] mt-0.5" data-id="element-2034" data-mp="2s3hqggtiq70e">
                    Charge the customer's card on file
                  </div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${data.method === 'saved-card' ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-2035" data-mp="31ucarfn7ewj7">
                  {data.method === 'saved-card' && <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" data-id="element-2036" data-mp="3avtquy0m2y7w" />}
                </div>
              </div>
            </button>

            {/* Expanded Content */}
            {data.method === 'saved-card' && <div className="px-4 pb-4 animate-[ocean-fade-in_200ms_ease-in-out]" data-id="element-2037" data-mp="1fsnl4slu0xvv">
                <div className="border-t border-[#E2E8F0] pt-4 space-y-4" data-id="element-2038" data-mp="1bqtocverce0z">
                  {/* Saved Cards Section */}
                  {hasSavedCards && <>
                      <div data-id="element-2039" data-mp="1pj9yeg62tcbe">
                        <p className="text-[12px] font-medium text-[#94A3B8] uppercase tracking-wide mb-2" data-id="element-2040" data-mp="1kq6yzremb8na">
                          Saved cards
                        </p>
                        <div className="space-y-2" data-id="element-2041" data-mp="1ugigkgr85gcn">
                          {MOCK_SAVED_CARDS.map(card => <button key={card.id} onClick={() => setSelectedCardId(card.id)} className={`w-full flex items-center gap-3 p-3 rounded-md border transition-all text-left ${selectedCardId === card.id ? 'border-[#0EA5E9] bg-white shadow-sm border-l-[3px]' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'}`} data-id="element-2042" data-mp="1uimgknm05v7n">
                              {/* Card brand icon placeholder */}
                              <div className={`w-10 h-7 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0 ${card.brand === 'Visa' ? 'bg-[#1A1F71] text-white' : 'bg-[#EB001B] text-white'}`} data-id="element-2043" data-mp="95065aypr3fa">
                                {card.brand === 'Visa' ? 'VISA' : 'MC'}
                              </div>
                              <div className="flex-1" data-id="element-2044" data-mp="2fzsqtpfzwmh6">
                                <span className="text-sm font-medium text-[#0F172A]" data-id="element-2045" data-mp="2fy99dvils5dt">
                                  {card.brand} ending in {card.last4}
                                </span>
                                <span className="text-xs text-[#94A3B8] ml-2" data-id="element-2046" data-mp="26zx911rxpt86">
                                  Exp {card.exp}
                                </span>
                              </div>
                              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selectedCardId === card.id ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-2047" data-mp="1x1pyp1s5i9f5">
                                {selectedCardId === card.id && <div className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9]" data-id="element-2048" data-mp="3rcmtqqe5e2p3" />}
                              </div>
                            </button>)}
                        </div>
                        <p className="text-[11px] text-[#94A3B8] mt-2" data-id="element-2049" data-mp="2xn5jxa26xcws">
                          From customer's Stripe account
                        </p>
                      </div>

                      {/* Divider */}
                      <div className="relative" data-id="element-2050" data-mp="2jph085jv0btb">
                        <div className="absolute inset-0 flex items-center" data-id="element-2051" data-mp="2jq8vtgdgi9jl">
                          <div className="w-full border-t border-[#E2E8F0]" data-id="element-2052" data-mp="2eyh0ytn9phnn" />
                        </div>
                        <div className="relative flex justify-center" data-id="element-2053" data-mp="2a0pb0566o5m3">
                          <span className="bg-[#F0F9FF] px-3 text-[12px] text-[#94A3B8] font-medium" data-id="element-2054" data-mp="250t869982k84">
                            or add new card
                          </span>
                        </div>
                      </div>
                    </>}

                  {/* New Card Section - Stripe Elements Placeholder */}
                  <div data-id="element-2055" data-mp="bgspenjoyoxk">
                    {!hasSavedCards && <p className="text-[12px] font-medium text-[#94A3B8] uppercase tracking-wide mb-2" data-id="element-2056" data-mp="bk6onmx26i0s">
                        Add card
                      </p>}
                    <div className="space-y-3" data-id="element-2057" data-mp="kjtpo7lvlltb">
                      {/* Card Number */}
                      <div className="relative" data-id="element-2058" data-mp="pgag58gjsc9k">
                        <input type="text" placeholder="Card number" disabled className="w-full h-10 px-3 pr-24 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-2059" data-mp="3nz9039y61xez" />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5" data-id="element-2060" data-mp="1rraz1ssoflp">
                          <div className="w-8 h-5 rounded-sm bg-[#1A1F71] flex items-center justify-center" data-id="element-2061" data-mp="6jj0ra44mhas">
                            <span className="text-[7px] font-bold text-white" data-id="element-2062" data-mp="35883gskiqdyl">
                              VISA
                            </span>
                          </div>
                          <div className="w-8 h-5 rounded-sm bg-[#EB001B] flex items-center justify-center" data-id="element-2063" data-mp="3263a5rtkateh">
                            <span className="text-[7px] font-bold text-white" data-id="element-2064" data-mp="2y4rrk693v8fs">
                              MC
                            </span>
                          </div>
                          <div className="w-8 h-5 rounded-sm bg-[#006FCF] flex items-center justify-center" data-id="element-2065" data-mp="36xl9ycjumnxv">
                            <span className="text-[7px] font-bold text-white" data-id="element-2066" data-mp="2j9budsd8awf6">
                              AMEX
                            </span>
                          </div>
                        </div>
                      </div>
                      {/* Expiry + CVC */}
                      <div className="grid grid-cols-2 gap-3" data-id="element-2067" data-mp="2o6u6tsqgftrx">
                        <input type="text" placeholder="MM / YY" disabled className="w-full h-10 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-2068" data-mp="2o9g9bdyhnj3a" />
                        <input type="text" placeholder="CVC" disabled className="w-full h-10 px-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-md text-sm text-[#94A3B8] placeholder-[#CBD5E1] outline-none cursor-default" data-id="element-2069" data-mp="1lf11ohffjqr4" />
                      </div>
                    </div>
                    {/* Security Note */}
                    <div className="flex items-center gap-1.5 mt-3" data-id="element-2070" data-mp="1hce7waz77vra">
                      <Lock className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-2071" data-mp="252qnm80oqwsf" />
                      <span className="text-[11px] text-[#94A3B8]" data-id="element-2072" data-mp="253jxhhuxy2es">
                        Securely processed by Stripe
                      </span>
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      <div data-id="element-2073" data-mp="20aquayf4f24t">
        <h3 className="text-base font-semibold text-[#0F172A] mb-4" data-id="element-2074" data-mp="20ah3j8m94qy2">
          Schedule
        </h3>
        <div className="space-y-4" data-id="element-2075" data-mp="2o01p7z9si2b1">
          <div data-id="element-2076" data-mp="2sscn6wkyze0y">
            <label className="block text-sm font-medium text-[#374151] mb-1.5" data-id="element-2077" data-mp="2ssurmghscyjy">
              First billing date
            </label>
            <div className="relative" data-id="element-2078" data-mp="sqv7mgfwb36p">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" data-id="element-2079" data-mp="srwgqfdcpfls" />
              <input type="date" value={data.firstBillingDate} onChange={e => onChange({
              ...data,
              firstBillingDate: e.target.value
            })} className="w-full h-10 pl-10 pr-3 bg-white border border-[#CBD5E1] rounded-md text-sm focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9]/20 outline-none" data-id="element-2080" data-mp="j09zo2ec0mb4" />
            </div>
            <p className="mt-1 text-xs text-[#64748B]" data-id="element-2081" data-mp="16r5z9lp14lsx">
              Customer will be charged on this date
            </p>
          </div>

          <div data-id="element-2082" data-mp="11smdpmeclc13">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-2083" data-mp="xpg7ger8jkum">
              <input type="checkbox" checked={data.sendWelcomeEmail} onChange={e => onChange({
              ...data,
              sendWelcomeEmail: e.target.checked
            })} className="w-4 h-4 rounded border-[#CBD5E1] text-[#0EA5E9] focus:ring-[#0EA5E9]" data-id="element-2084" data-mp="xr98nej2xcv8" />
              <div data-id="element-2085" data-mp="1xynyvjfiqgeu">
                <div className="text-sm font-medium text-[#0F172A]" data-id="element-2086" data-mp="1xz7ir5oqbevi">
                  Send welcome email
                </div>
                <div className="text-xs text-[#64748B]" data-id="element-2087" data-mp="26y1d0pel7t5w">
                  Customer will receive a welcome email with portal access
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>;
}
function PaymentOption({
  icon,
  value,
  selected,
  onSelect,
  title,
  description
}: any) {
  return <button onClick={onSelect} className={`w-full p-4 border-2 rounded-lg text-left transition-all ${selected ? 'border-[#0EA5E9] bg-[#F0F9FF]' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-2088" data-mp="2llmcm9c97a92">
      <div className="flex items-start gap-3" data-id="element-2089" data-mp="2umkhetuinlsp">
        <div className={`flex-shrink-0 ${selected ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-2090" data-mp="2ul9gr2wdau5t">
          {icon}
        </div>
        <div className="flex-1" data-id="element-2091" data-mp="1092t34maekwm">
          <div className="text-sm font-medium text-[#0F172A]" data-id="element-2092" data-mp="10adxn6nk3wos">{title}</div>
          <div className="text-sm text-[#64748B] mt-0.5" data-id="element-2093" data-mp="k3nm0vkon4g9">{description}</div>
        </div>
        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${selected ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} data-id="element-2094" data-mp="p1fnohb4q4xn">
          {selected && <div className="w-3 h-3 rounded-full bg-[#0EA5E9]" data-id="element-2095" data-mp="tz7ef801fl09" />}
        </div>
      </div>
    </button>;
}
// Step 5: Review
function Step5Review({
  customerData,
  addressData,
  subscriptionData,
  paymentData,
  onEdit,
  getSubtotal
}: any) {
  return <div className="space-y-4" data-id="element-2096" data-mp="17rfbl0v8hdzh">
      <ReviewCard title="Customer" onEdit={() => onEdit(1)} content={<div className="space-y-2" data-id="element-2098" data-mp="1csl34vd5qv7f">
            <div className="text-sm font-medium text-[#0F172A]" data-id="element-2099" data-mp="1cpgr5btgav67">
              {customerData.firstName} {customerData.lastName}
            </div>
            <div className="text-sm text-[#64748B]" data-id="element-2100" data-mp="3ejofbp2p4hu3">{customerData.email}</div>
            {customerData.phone && <div className="text-sm text-[#64748B]" data-id="element-2101" data-mp="3jh7t6nvhesli">{customerData.phone}</div>}
            {customerData.tags.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-2102" data-mp="20nwt14cvgzge">
                {customerData.tags.map((tag: TagData) => <span key={tag.id} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${tag.color === 'blue' ? 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]' : tag.color === 'green' ? 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]' : tag.color === 'orange' ? 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]' : tag.color === 'purple' ? 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]' : 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'}`} data-id="element-2103" data-mp="1vv3nwfdhrp3s">
                    {tag.label}
                  </span>)}
              </div>}
            {customerData.acquisitionSource && <div className="flex items-center gap-4 text-sm mt-2" data-id="element-2104" data-mp="25mhlghlvueua">
                <span className="text-[#64748B]" data-id="element-2105" data-mp="25oc4fqdlfwvq">Source:</span>
                <span className="text-[#0F172A] font-medium" data-id="element-2106" data-mp="z6p4tw4stjc7">
                  {customerData.acquisitionSource}
                  {customerData.acquisitionSourceDetail && ` — ${customerData.acquisitionSourceDetail}`}
                </span>
              </div>}
          </div>} data-id="element-2097" data-mp="17ujl8drgx1xj" />

      <ReviewCard title="Shipping Address" onEdit={() => onEdit(2)} content={<div className="space-y-1" data-id="element-2108" data-mp="mna6wvlbke9a">
            <div className="text-sm text-[#0F172A]" data-id="element-2109" data-mp="mm8lpyalabdu">
              {addressData.shipping.line1}
            </div>
            {addressData.shipping.line2 && <div className="text-sm text-[#0F172A]" data-id="element-2110" data-mp="ro7nj3qol08o">
                {addressData.shipping.line2}
              </div>}
            <div className="text-sm text-[#0F172A]" data-id="element-2111" data-mp="hnxabj4nbsti">
              {addressData.shipping.city}, {addressData.shipping.state}{' '}
              {addressData.shipping.postcode}
            </div>
            <div className="text-sm text-[#0F172A]" data-id="element-2112" data-mp="7ydruekui0z6">
              {addressData.shipping.country}
            </div>
            {addressData.billingSameAsShipping && <div className="text-sm text-[#64748B] mt-2" data-id="element-2113" data-mp="3q4inzd883a2y">
                Billing: Same as shipping
              </div>}
          </div>} data-id="element-2107" data-mp="10nba4hp23xrx" />

      <ReviewCard title="Subscription" onEdit={() => onEdit(3)} content={<div className="space-y-3" data-id="element-2115" data-mp="3bhtmx6ybooug">
            <div className="flex items-center gap-4 text-sm" data-id="element-2116" data-mp="36i8gkbgtpe7p">
              <span className="text-[#64748B]" data-id="element-2117" data-mp="36k1m1nfp9se4">Preference:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2118" data-mp="3lfrartc9sjmj">
                {subscriptionData.preference}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-2119" data-mp="3gihdxsmjx5oo">
              <span className="text-[#64748B]" data-id="element-2120" data-mp="3uf4e049swbyt">Bottle Count:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2121" data-mp="3phcjd2hrubqx">
                {subscriptionData.bottleCount} Bottles
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-2122" data-mp="801nx8lrr4mr">
              <span className="text-[#64748B]" data-id="element-2123" data-mp="318b7ieb3hnn">Frequency:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2124" data-mp="33ukpnkrc78i">
                {subscriptionData.frequency}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-2125" data-mp="d0pghypnbtj3">
              <span className="text-[#64748B]" data-id="element-2126" data-mp="26iemmgs3nc0j">Shipping Method:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2127" data-mp="26i4ykyqm7exh">
                {subscriptionData.shippingMethod}
              </span>
            </div>
            <div className="pt-2 border-t border-[#E2E8F0]" data-id="element-2128" data-mp="2g7ohu5i8pasv">
              <div className="text-sm font-medium text-[#0F172A] mb-2" data-id="element-2129" data-mp="2gaaw0nzmyok2">
                Selected Wines:
              </div>
              <div className="space-y-1" data-id="element-2130" data-mp="1njskqzordgvk">
                {subscriptionData.wines.map((wine: any) => <div key={wine.id} className="flex justify-between text-sm text-[#64748B]" data-id="element-2131" data-mp="1sim0yzvfjlzf">
                    <span data-id="element-2132" data-mp="1skgl4byvna2g">
                      {wine.vintage} {wine.name} × {wine.quantity}
                    </span>
                    <span data-id="element-2133" data-mp="21j1yzttem0ww">${(wine.price * wine.quantity).toFixed(2)}</span>
                  </div>)}
              </div>
              <div className="flex justify-between text-sm font-medium text-[#0F172A] mt-2 pt-2 border-t border-[#E2E8F0]" data-id="element-2134" data-mp="38xn987jwpk0p">
                <span data-id="element-2135" data-mp="3cww27a74hdv7">Subtotal:</span>
                <span data-id="element-2136" data-mp="11b3zsxpaljbe">${getSubtotal().toFixed(2)}</span>
              </div>
            </div>
          </div>} data-id="element-2114" data-mp="3bhbkfsl2hee3" />

      <ReviewCard title="Payment" onEdit={() => onEdit(4)} content={<div className="space-y-2" data-id="element-2138" data-mp="xabpe14op8dj">
            <div className="flex items-center gap-4 text-sm" data-id="element-2139" data-mp="sijwhjy22qk8">
              <span className="text-[#64748B]" data-id="element-2140" data-mp="sgz4o234llq2">Payment Method:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2141" data-mp="ninutrctzb7u">
                {paymentData.method === 'invoice' ? 'Send invoice' : 'Use saved payment method'}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-2142" data-mp="dkhqz47grv0c">
              <span className="text-[#64748B]" data-id="element-2143" data-mp="9jxxqbq0hbjh">First Billing Date:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2144" data-mp="9m1wygcytmdf">
                {new Date(paymentData.firstBillingDate).toLocaleDateString('en-AU')}
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm" data-id="element-2145" data-mp="23xgpf4xd1zwv">
              <span className="text-[#64748B]" data-id="element-2146" data-mp="1yyccq4b89k1s">Welcome Email:</span>
              <span className="text-[#0F172A] font-medium" data-id="element-2147" data-mp="1yx1eek0jx4i2">
                {paymentData.sendWelcomeEmail ? 'Will be sent' : 'Will not be sent'}
              </span>
            </div>
          </div>} data-id="element-2137" data-mp="x9s72jb4ghyz" />

      {customerData.internalNote && <ReviewCard title="Internal Note" content={<div className="text-sm text-[#64748B]" data-id="element-2149" data-mp="294nh0lz6tbef">
              {customerData.internalNote}
            </div>} data-id="element-2148" data-mp="293cbohq3fvl7" />}
    </div>;
}
function ReviewCard({
  title,
  onEdit,
  content
}: any) {
  return <div className="p-4 bg-white border border-[#E2E8F0] rounded-lg" data-id="element-2150" data-mp="31rzs9zzwdwsf">
      <div className="flex items-center justify-between mb-3" data-id="element-2151" data-mp="35tcyib6wk1zv">
        <h4 className="text-sm font-semibold text-[#0F172A]" data-id="element-2152" data-mp="35qqp060gbcf0">{title}</h4>
        {onEdit && <button onClick={onEdit} className="text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-2153" data-mp="2rven9envolol">
            <Pencil className="w-4 h-4" data-id="element-2154" data-mp="2wuhriww1anvi" />
          </button>}
      </div>
      {content}
    </div>;
}
// Success View
function SuccessView({
  subscriptionId,
  customerName,
  onViewSubscription,
  onCreateAnother
}: any) {
  return <div className="p-12 flex flex-col items-center justify-center text-center" data-id="element-2155" data-mp="1dtgwz4yywqpu">
      <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mb-6" data-id="element-2156" data-mp="1dvjwqgpx9y7r">
        <Check className="w-8 h-8 text-[#059669]" data-id="element-2157" data-mp="18wyrg2vtcocg" />
      </div>
      <h2 className="text-[20px] font-semibold text-[#0F172A] mb-2" data-id="element-2158" data-mp="1mtvia4bxm5tc">
        Subscription Created
      </h2>
      <div className="text-[#0EA5E9] font-medium mb-1" data-id="element-2159" data-mp="1hw3rjdn0wpqq">{subscriptionId}</div>
      <p className="text-sm text-[#64748B] mb-8" data-id="element-2160" data-mp="u6rhycnsafnw">for {customerName}</p>
      <div className="flex flex-col sm:flex-row gap-3 w-full max-w-sm" data-id="element-2161" data-mp="q4o8ftkodl59">
        <Button variant="outline" size="md" onClick={onCreateAnother} className="flex-1" data-id="element-2162" data-mp="q2bhiqwxw0g2">
          Create Another
        </Button>
        <Button variant="primary" size="md" onClick={onViewSubscription} className="flex-1" data-id="element-2163" data-mp="z4b1ns393kn8">
          Done
        </Button>
      </div>
    </div>;
}
// Discard Confirmation
function DiscardConfirmation({
  onCancel,
  onDiscard
}: any) {
  return <div className="absolute inset-0 z-[1060] flex items-center justify-center p-4" data-id="element-2164" data-mp="6fvl314h0u9s">
      <div className="absolute inset-0 bg-[#0F172A]/60" onClick={onCancel} data-id="element-2165" data-mp="3i113kqb8tomt" />
      <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full" data-id="element-2166" data-mp="3m0jh310qixfm">
        <h3 className="text-lg font-semibold text-[#0F172A] mb-2" data-id="element-2167" data-mp="3qzuzeegr2azb">
          Discard changes?
        </h3>
        <p className="text-sm text-[#64748B] mb-6" data-id="element-2168" data-mp="3w00llf4935a0">
          Any information entered will be lost.
        </p>
        <div className="flex gap-3 justify-end" data-id="element-2169" data-mp="4s4ua5jyl7qm">
          <Button variant="outline" size="md" onClick={onCancel} data-id="element-2170" data-mp="4rd81lc94md0">
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={onDiscard} data-id="element-2171" data-mp="8sf1fljhm472">
            Discard
          </Button>
        </div>
      </div>
    </div>;
}